<script setup lang="ts">
import UserHeader from "@/Pages/Users/Partials/UserHeader.vue";
import { computed } from "vue";
import AppContainer from "@/Components/Shared/container/AppContainer.vue";
import AppPage from "@/Components/Shared/page/AppPage.vue";
import AppGrid from "@/Components/Shared/grid/AppGrid.vue";
import AppColumn from "@/Components/Shared/grid/AppColumn.vue";
import AppLink from "@/Components/Shared/link/AppLink.vue";
import AppHeader from "@/Components/Shared/header/AppHeader.vue";
import AppTitle from "@/Components/Shared/title/AppTitle.vue";
import AppPlaceholder from "@/Components/Shared/placeholder/AppPlaceholder.vue";
import TheLayoutApp from "@/Layouts/TheLayoutApp.vue";
import AppReviewGrid from "@/Components/App/reviews/AppReviewGrid.vue";
import AppUserList from "@/Components/App/users/AppUserList.vue";
import AppProductCard from "@/Components/Shared/product/AppProductCard.vue";
import AppProductGrid from "@/Components/Shared/product/AppProductGrid.vue";
import AppDivider from "@/Components/Shared/divider/AppDivider.vue";

type Props = {
    user: object;
    latestReviews?: object;
    relatedUsers?: object;
};

const { user, latestReviews = undefined } = defineProps<Props>();

const userName = computed(() => {
    return user.data.name
        ? `${user.data.name} (@${user.data.username})`
        : `@${user.data.username}`;
});

const breadcrumbs = [
    {
        label: "Community",
        path: "/community",
    },
];
</script>

<template>
    <TheLayoutApp
        :title="userName"
        :description="`Benieuwd wat de aanraders van ${userName} zijn? Bekijk hier alle aangeraden producten, aangemaakte collecties en meer!`"
        :breadcrumbs="breadcrumbs"
    >
        <AppPage size="small">
            <AppContainer>
                <UserHeader :user="user.data" />

                <AppGrid>
                    <AppColumn
                        v-if="
                            user.data.profile &&
                            user.data.profile.featured_product
                        "
                        class="md:col-span-5 lg:col-span-4"
                    >
                        <div
                            class="flex flex-col mb-8 p-4 border border-gray-lightest rounded"
                        >
                            <AppHeader
                                v-if="
                                    user.data.profile.featured_product.comment
                                "
                                size="small"
                            >
                                <AppTitle
                                    as="h4"
                                    size="xsmall"
                                    >{{
                                        user.data.profile.featured_product
                                            .comment
                                    }}
                                </AppTitle>
                            </AppHeader>

                            <AppProductCard
                                :product="user.data.profile.featured_product"
                                hide-options
                            />
                        </div>
                    </AppColumn>
                    <AppColumn
                        :class="
                            user.data.profile &&
                            user.data.profile.featured_product
                                ? 'md:col-span-7 lg:col-span-8'
                                : 'md:col-span-12 lg:col-span-9'
                        "
                    >
                        <section
                            v-if="
                                user.data.profile.favorite_products &&
                                user.data.profile.favorite_products.data.length
                            "
                            class="mb-8 sm:mb-12"
                        >
                            <AppHeader>
                                <AppTitle
                                    as="h3"
                                    variant="main"
                                    >Favoriete producten</AppTitle
                                >
                            </AppHeader>
                            <AppProductGrid
                                :products="
                                    user.data.profile.favorite_products.data
                                "
                                size="four"
                                card-size="small"
                            />
                        </section>

                        <section>
                            <AppHeader>
                                <AppTitle
                                    as="h2"
                                    variant="main"
                                >
                                    Laatste reviews
                                </AppTitle>
                            </AppHeader>

                            <template
                                v-if="
                                    latestReviews && latestReviews.data.length
                                "
                            >
                                <AppReviewGrid
                                    :reviews="latestReviews.data"
                                    context="user"
                                />

                                <AppDivider>
                                    <AppLink
                                        :href="`${user.data.path}/reviews`"
                                        variant="buttonSmall"
                                        icon-after="arrow-right"
                                    >
                                        Bekijk alle reviews
                                    </AppLink>
                                </AppDivider>
                            </template>
                            <AppPlaceholder v-else>
                                (Nog) geen reviews gevonden
                            </AppPlaceholder>
                        </section>
                    </AppColumn>
                </AppGrid>
            </AppContainer>
        </AppPage>
        <template #sidebar>
            <section v-if="relatedUsers && relatedUsers.data.length">
                <AppHeader>
                    <AppTitle
                        as="h3"
                        variant="subtitle"
                    >
                        Gerelateerde leden
                    </AppTitle>
                </AppHeader>

                <AppUserList :users="relatedUsers.data" />
            </section>
        </template>
    </TheLayoutApp>
</template>
